import { useSyncExternalStore } from 'react';
import { PMS } from '@/features/shared/pms';

/**
 * One source for "who is in network with which payer" (D196, H4). Settings ›
 * Practice details › Networks edits it; Posting, Claims, Insurance A/R and the
 * Dashboard read it. In-memory mock; a change is logged with person + time.
 */
export type NetworkStatus = 'in' | 'out' | 'unconfirmed';

export const NETWORK_PAYERS = [
  'Aetna', 'BCBS of Texas', 'Cigna', 'Delta Dental of Texas', 'Guardian',
  'Humana', 'MetLife', 'Sun Life', 'UnitedHealthcare', 'United Concordia',
] as const;
export type NetworkPayer = (typeof NETWORK_PAYERS)[number];

export const NETWORK_DENTISTS = ['Dr. Emily Carter', 'Dr. Olivia Brooks', 'Dr. Marcus Lee'] as const;
export type NetworkDentist = (typeof NETWORK_DENTISTS)[number];

export interface NetworkCell {
  status: NetworkStatus;
  /** "Mar 2019" — in network only. */
  since?: string;
  /** Fee schedule name — in network only. */
  feeSchedule?: string;
  /** Where today's value came from. */
  source: string;
}

export interface NetworkChange {
  dentist: NetworkDentist;
  payer: NetworkPayer;
  from: NetworkStatus;
  to: NetworkStatus;
  by: string;
  /** The person's organisation, e.g. "Lone Star Billing". */
  org?: string;
  at: string;
}

const FEE: Record<string, string> = {
  Aetna: 'Aetna PPO 2026',
  'BCBS of Texas': 'BCBS TX PPO 2026',
  Cigna: 'Cigna DPPO 2026',
  'Delta Dental of Texas': 'Delta Premier TX 2026',
  Guardian: 'Guardian DentalGuard 2026',
  Humana: 'Humana Dental 2026',
  MetLife: 'MetLife PDP Plus 2026',
  'Sun Life': 'Sun Life PPO 2026',
  UnitedHealthcare: 'UHC Dental Options 2026',
  'United Concordia': 'UCCI Advantage 2026',
};

const SINCE: Record<string, string> = {
  Aetna: 'Mar 2019',
  'BCBS of Texas': 'Jan 2020',
  Cigna: 'Jun 2018',
  'Delta Dental of Texas': 'Feb 2018',
  Guardian: 'Aug 2021',
  Humana: 'Apr 2022',
  MetLife: 'Sep 2020',
  'Sun Life': 'Nov 2022',
  UnitedHealthcare: 'May 2021',
  'United Concordia': 'Oct 2023',
};

function inCell(p: NetworkPayer): NetworkCell {
  return {
    status: 'in',
    since: SINCE[p] ?? 'Jan 2020',
    feeSchedule: FEE[p] ?? '',
    source: `From the plan's fee schedule in ${PMS}`,
  };
}
const outCell = (): NetworkCell => ({ status: 'out', source: `From the plan's fee schedule in ${PMS} (none attached)` });
const unconfirmedCell = (): NetworkCell => ({ status: 'unconfirmed', source: 'ELVA guessed from the PMS; nobody has confirmed it' });

const LEE_IN: ReadonlySet<string> = new Set(['Aetna', 'BCBS of Texas', 'Cigna', 'Delta Dental of Texas', 'UnitedHealthcare']);

function seed(): Record<string, NetworkCell> {
  const m: Record<string, NetworkCell> = {};
  for (const p of NETWORK_PAYERS) {
    // Dr. Emily Carter: in network with 9 (all but United Concordia).
    m[`Dr. Emily Carter|${p}`] = p === 'United Concordia' ? outCell() : inCell(p);
    // Dr. Olivia Brooks: in with 7; out with Sun Life and United Concordia; Humana not confirmed.
    m[`Dr. Olivia Brooks|${p}`] =
      p === 'Sun Life' || p === 'United Concordia' ? outCell() : p === 'Humana' ? unconfirmedCell() : inCell(p);
    // Dr. Marcus Lee: in with 5, out with the other 5.
    m[`Dr. Marcus Lee|${p}`] = LEE_IN.has(p) ? inCell(p) : outCell();
  }
  return m;
}

let cells = seed();
let changes: NetworkChange[] = [];
let version = 0;
const listeners = new Set<() => void>();
function bump() { version++; listeners.forEach((l) => { l(); }); }

export function useNetworksVersion(): number {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => version,
    () => version,
  );
}

export function getNetworkCell(dentist: string, payer: string): NetworkCell | undefined {
  return cells[`${dentist}|${canonPayer(payer)}`];
}
export function getNetworkStatus(dentist: string, payer: string): NetworkStatus {
  return getNetworkCell(dentist, payer)?.status ?? 'in';
}
/** Out of network = no contractual write-off (D196). Not confirmed is treated as in network. */
export function isOutOfNetwork(dentist: string, payer: string): boolean {
  return getNetworkStatus(dentist, payer) === 'out';
}

/** "Delta Dental" / "Delta Dental of Texas" etc. → the one network payer name. */
export function canonPayer(name: string): string {
  const n = name.trim();
  if (/^delta/i.test(n)) return 'Delta Dental of Texas';
  if (/^bcbs|blue cross/i.test(n)) return 'BCBS of Texas';
  if (/^united ?concordia|^ucci/i.test(n)) return 'United Concordia';
  if (/^united ?health|^uhc/i.test(n)) return 'UnitedHealthcare';
  return n;
}

export function setNetworkStatus(
  dentist: NetworkDentist,
  payer: NetworkPayer,
  next: { status: 'in' | 'out'; since?: string; feeSchedule?: string },
  by: string,
  org?: string,
): void {
  const key = `${dentist}|${payer}`;
  const prev = cells[key];
  cells = {
    ...cells,
    [key]:
      next.status === 'in'
        ? { status: 'in', since: next.since ?? 'Oct 2026', feeSchedule: next.feeSchedule ?? FEE[payer] ?? '', source: 'Set by your team' }
        : { status: 'out', source: 'Set by your team' },
  };
  changes = [
    { dentist, payer, from: prev?.status ?? 'in', to: next.status, by, ...(org ? { org } : {}), at: 'Oct 21, 2026' },
    ...changes,
  ];
  bump();
}
export function getNetworkChanges(): NetworkChange[] { return changes; }

/** In network count for the Dentists table: confirmed in-network payers, and how many aren't confirmed. */
export function networkSummary(dentist: string): { inCount: number; unconfirmed: number } {
  let inCount = 0;
  let unconfirmed = 0;
  for (const p of NETWORK_PAYERS) {
    const s = cells[`${dentist}|${p}`]?.status;
    if (s === 'in') inCount++;
    else if (s === 'unconfirmed') unconfirmed++;
  }
  return { inCount, unconfirmed };
}
export function unconfirmedCount(): number {
  return NETWORK_DENTISTS.reduce((n, d) => n + networkSummary(d).unconfirmed, 0);
}
export function resetNetworks(): void { cells = seed(); changes = []; bump(); }
